import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  LayoutDashboard, Table2, Database, Layers, Repeat, History, Trash2,
  PencilLine, Settings, ChevronLeft, ChevronRight, Landmark, Briefcase,
} from 'lucide-react'
import { useUIStore } from '../stores/ui'
import { useAuthStore } from '../stores/auth'
import { canAccessPageWithSecret } from '../lib/pages'

interface NavPage {
  path: string
  label: string
  icon: any
  key: string
}

interface NavSection {
  label: string
  pages: NavPage[]
}

// セクション分けされたサイドバー
// メイン: 日常的に使うページ。銀行口座は重要マスタとしてここに配置
// マスタ: マスタデータの編集
// 取引管理: 自動化系の取引（定期・リスケ）
// データ: 履歴・ゴミ箱
// システム: 設定
const ALL_NAV_SECTIONS: NavSection[] = [
  {
    label: 'メイン',
    pages: [
      { path: '/dashboard', label: 'ダッシュボード', icon: LayoutDashboard, key: 'dashboard' },
      { path: '/table', label: '資金繰り表', icon: Table2, key: 'table' },
    ],
  },
  {
    label: 'マスタ',
    pages: [
      { path: '/masters', label: '取引先・カテゴリ', icon: Database, key: 'masters' },
    ],
  },
  {
    label: '取引管理',
    pages: [
      { path: '/input', label: '収支登録', icon: PencilLine, key: 'input' },
      { path: '/banks', label: '銀行口座', icon: Landmark, key: 'banks' },
      { path: '/recurring', label: '定期取引', icon: Repeat, key: 'recurring' },
      { path: '/reschedule', label: 'リスケ管理', icon: Layers, key: 'debt_reschedule' },
    ],
  },
  {
    label: 'データ',
    pages: [
      { path: '/history', label: '取引履歴', icon: History, key: 'history' },
      { path: '/trash', label: 'ゴミ箱', icon: Trash2, key: 'trash' },
    ],
  },
  {
    label: 'システム',
    pages: [
      { path: '/settings', label: '設定', icon: Settings, key: 'settings' },
    ],
  },
]

export default function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const user = useAuthStore((s) => s.user)

  // シークレット権限あり = シークレットページを常時閲覧可能。トグルボタンは不要。
  const isAdmin = user?.role === 'admin'
  const hasSecretPermission = isAdmin || !!user?.canViewSecret

  // SSO経由でログインした場合（アドバイザーポータルから来た）にだけ
  // 「アドバイザーポータルに戻る」リンクを表示する。
  // sessionStorage を使うのでブラウザを閉じると消える（タブを開き直すと再度ログインが必要）。
  const [advisorUrl, setAdvisorUrl] = useState<string | null>(null)
  useEffect(() => {
    try {
      if (sessionStorage.getItem('advisor_origin') === '1') {
        setAdvisorUrl(sessionStorage.getItem('advisor_url') || 'https://cashflow-advisor.pages.dev')
      }
    } catch { /* ignore */ }
  }, [])
  const sections = ALL_NAV_SECTIONS
    .map((s) => ({
      ...s,
      pages: s.pages.filter((p) =>
        canAccessPageWithSecret(user?.role, user?.allowedPages, hasSecretPermission, p.key),
      ),
    }))
    .filter((s) => s.pages.length > 0)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (sidebarOpen && window.matchMedia('(max-width: 1023px)').matches) {
      toggleSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNav = (path: string) => {
    navigate(path)
    if (window.matchMedia('(max-width: 1023px)').matches && sidebarOpen) {
      toggleSidebar()
    }
  }

  return (
    <>
      {sidebarOpen && (
        <div
          onClick={toggleSidebar}
          className="lg:hidden fixed inset-0 z-20 bg-black/40 transition-opacity"
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 bg-[var(--sb-bg)] text-[var(--sb-fg)] flex flex-col transition-all duration-200 w-56 ${
          sidebarOpen
            ? 'translate-x-0 lg:w-56'
            : '-translate-x-full lg:translate-x-0 lg:w-14'
        }`}
      >
        <div className="h-14 flex items-center justify-between px-3 border-b border-[var(--sb-line)]">
          <div
            className={`font-bold text-white truncate ${
              sidebarOpen ? 'text-base px-2' : 'text-xs text-center w-full'
            }`}
          >
            {sidebarOpen ? '資金繰り管理' : '¥'}
          </div>
          <button
            onClick={toggleSidebar}
            className="text-[var(--sb-fg-muted)] hover:text-white flex-shrink-0 p-1 rounded hover:bg-[var(--sb-hover)] transition"
            aria-label={sidebarOpen ? 'サイドバーを閉じる' : 'サイドバーを開く'}
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* アドバイザーポータルに戻るリンク（SSO経由ログインのみ表示） */}
        {advisorUrl && (
          <div className="px-2 pt-2">
            <a
              href={advisorUrl}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition border border-[var(--sb-line)] bg-[var(--sb-chip)] text-[var(--sb-fg)] hover:bg-[var(--sb-hover)]"
              title={!sidebarOpen ? 'アドバイザーポータルに戻る' : undefined}
            >
              <Briefcase className="w-[18px] h-[18px] flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">アドバイザーへ戻る</span>}
            </a>
          </div>
        )}

        <nav className="flex-1 p-2 overflow-y-auto">
          {sections.map((section, sIdx) => (
            <div key={section.label} className={sIdx > 0 ? 'mt-3' : ''}>
              {sidebarOpen && (
                <div className="text-[10px] font-semibold text-[var(--sb-fg-muted)] uppercase tracking-wider px-3 py-1">
                  {section.label}
                </div>
              )}
              {!sidebarOpen && sIdx > 0 && (
                <div className="border-t border-[var(--sb-line)] my-1.5 mx-1" aria-hidden="true" />
              )}
              <div className="space-y-0.5">
                {section.pages.map(({ path, label, icon: Icon, key }) => {
                  const active =
                    location.pathname === path ||
                    (key === 'debt_reschedule' && (location.pathname.startsWith('/debt') || location.pathname.startsWith('/reschedule'))) ||
                    (key === 'masters' && location.pathname.startsWith('/vendors')) ||
                    (key === 'banks' && location.pathname.startsWith('/banks'))
                  return (
                    <button
                      key={path}
                      onClick={() => handleNav(path)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                        active
                          ? 'bg-[var(--sb-active-bg)] text-[var(--sb-active-fg)] font-bold'
                          : 'text-[var(--sb-fg)] hover:bg-[var(--sb-hover)]'
                      }`}
                      title={!sidebarOpen ? label : undefined}
                    >
                      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                      {sidebarOpen && <span>{label}</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  )
}
